import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

function Header() {
  const { isAuthenticated } = useAuth();
  return (
    <header className="flex h-14 items-center justify-end px-6 lg:h-[60px] lg:px-8">
      <Button variant="outline" size="sm" asChild>
        {isAuthenticated ? (
          <Link to="/jams">Jams</Link>
        ) : (
          <Link to="/login">Log in</Link>
        )}
      </Button>
    </header>
  );
}

function HeroFeature({
  number,
  headline,
  children,
}: {
  number: string;
  headline: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-t pt-10 pb-4">
      <p className="text-xs font-mono text-muted-foreground/50 mb-3 tracking-widest">
        {number}
      </p>
      <h2 className="text-2xl font-bold uppercase tracking-[0.12em] text-foreground mb-4 leading-snug">
        {headline}
      </h2>
      <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
        {children}
      </p>
    </div>
  );
}

function FeatureGroup({
  title,
  features,
}: {
  title: string;
  features: string[];
}) {
  return (
    <div className="bg-[#191919] border border-[#616161]/20 rounded-lg p-5">
      <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
        {title}
      </h3>
      <ul className="space-y-2">
        {features.map((f) => (
          <li key={f} className="text-sm text-foreground/80 flex gap-2">
            <span className="text-primary shrink-0">—</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

type CellValue = "yes" | "no" | "partial" | string;

function Cell({ value }: { value: CellValue }) {
  if (value === "yes")
    return <span className="text-primary font-medium">✓</span>;
  if (value === "no")
    return <span className="text-muted-foreground/40">✗</span>;
  return (
    <span className="text-muted-foreground text-xs leading-tight">{value}</span>
  );
}

const COMPETITORS = [
  "Cobar",
  "Endlesss",
  "Boss RC-600",
  "Loopy Pro",
  "Ableton Looper",
];

const COMPARISON_ROWS: { label: string; values: CellValue[] }[] = [
  {
    label: "Retrospective recording",
    values: ["yes", "no", "no", "no", "no"],
  },
  { label: "Online jams", values: ["yes", "yes", "no", "no", "no"] },
  { label: "AU plugin", values: ["yes", "no", "N/A", "no", "yes"] },
  {
    label: "DAW integration",
    values: ["yes", "no", "no", "Ableton Link", "yes"],
  },
  { label: "Standalone app", values: ["yes", "yes", "yes", "yes", "no"] },
  { label: "MIDI Learn", values: ["yes", "no", "yes", "yes", "yes"] },
  {
    label: "Native effects chain",
    values: ["yes", "no", "yes", "yes", "no"],
  },
  { label: "VST/AU hosting", values: ["yes", "no", "no", "no", "no"] },
  {
    label: "Loop transformations",
    values: ["yes", "no", "no", "no", "no"],
  },
  {
    label: "Lossless audio (FLAC)",
    values: ["yes", "no", "N/A", "no", "N/A"],
  },
  { label: "96 kHz support", values: ["yes", "no", "no", "no", "yes"] },
  {
    label: "Hands-free (head nod)",
    values: ["yes", "no", "no", "no", "no"],
  },
  {
    label: "Tape deck varispeed",
    values: ["yes", "no", "no", "no", "no"],
  },
  { label: "Undo / Redo", values: ["yes", "no", "no", "yes", "no"] },
  { label: "Stem export", values: ["yes", "no", "no", "yes", "no"] },
];

export default function Features() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 px-6 lg:px-8 pb-24">
        <div className="max-w-5xl mx-auto">
          {/* Page title */}
          <div className="py-10 border-b mb-0">
            <h1 className="text-5xl font-bold uppercase tracking-[0.2em] text-foreground mb-3">
              Cobar
            </h1>
            <p className="text-sm text-muted-foreground uppercase tracking-wider">
              Features
            </p>
          </div>

          {/* Hero features */}
          <div className="mb-16">
            <HeroFeature number="01" headline="Always recording. Commit what matters.">
              Every note you play is already captured. Cobar keeps a rolling
              16-bar buffer running at all times — before you've decided to
              record. When inspiration strikes, commit the last 1–16 bars of
              whatever you've just played. No punching in. No rewind. No
              missed moments.
              <br />
              <br />
              For professional musicians: commits are bar-quantized,
              FLAC-compressed to lossless quality, and immediately baked into
              your DAW project state. The retrospective buffer pre-allocates
              for the worst-case BPM (40), so there are zero dynamic
              allocations in the audio thread — a requirement for any
              production-grade plugin.
            </HeroFeature>

            <HeroFeature number="02" headline="Collaborate across time zones, not just sessions.">
              Cobar jams don't require everyone to be online at once. Add
              loops to a shared session, and collaborators receive them
              whenever they connect. Contributions layer up over hours, days,
              or weeks — each player building on what the others left behind.
              <br />
              <br />
              This is a fundamentally different model from live session tools.
              There's no video call to schedule, no latency to fight, no
              windows to align. Just music, accumulating.
            </HeroFeature>

            <HeroFeature number="03" headline="Loops that transform each other.">
              Drag one loop onto another to blend, fuse, or mutate them.
              Replace overwrites. Add creates an additive mix. Multiply applies
              ring modulation. FreqMod uses the source's amplitude envelope to
              modulate the target's pitch. Convolve runs an FFT-based
              convolution between the two.
              <br />
              <br />
              Each loop can become the source material for the next. A drum
              pattern becomes a filter envelope. A bass line becomes a room
              impulse. The loop library is a palette, not a tape deck.
            </HeroFeature>
          </div>

          {/* Feature grid */}
          <div className="border-t pt-10 mb-16">
            <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground mb-6">
              More features
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <FeatureGroup
                title="Mixer & Routing"
                features={[
                  "Unlimited loops — up to 144 playing simultaneously",
                  "16 groups, 8 aux buses, full send matrix",
                  "Per-channel gain, pan, mute, solo",
                  "Drag-and-drop channel reordering",
                  "8 assignable MACRO dials",
                ]}
              />
              <FeatureGroup
                title="Effects"
                features={[
                  "9 native effects: Reverb, Delay, Filter, Compressor, Limiter, Tremolo, Tuner, Night Drive, Utility",
                  "AU and VST3 plugin hosting on every channel",
                  "Nonlinear TPT filter with 4 drive models",
                  "8-line modulated FDN reverb",
                  "Tempo-sync delay and tremolo",
                ]}
              />
              <FeatureGroup
                title="Recording"
                features={[
                  "Retrospective, Standard, and Free recording modes",
                  "Bar-quantized commit with visual countdown",
                  "Quantization: immediate, ¼, ½, or 1 bar",
                  "Drag-and-drop audio import",
                  "Auto-detect bar length from imported files",
                ]}
              />
              <FeatureGroup
                title="Control"
                features={[
                  "Full MIDI Learn — CC and Note, any channel",
                  "Multiple CC modes: Absolute, Relative (Three's Complement, Signed Bit, Binary Offset)",
                  "Head nod gesture for hands-free commits",
                  "100-level undo / redo",
                ]}
              />
              <FeatureGroup
                title="Audio Quality"
                features={[
                  "FLAC lossless compression — 50–60% smaller, zero quality loss",
                  "24-bit WAV drag-and-drop export",
                  "44.1 kHz, 48 kHz, and 96 kHz support",
                  "Brick-wall limiter at −0.1 dBFS",
                  "Always-active clip detection",
                ]}
              />
              <FeatureGroup
                title="Playback & Export"
                features={[
                  "Tape deck varispeed — BPM changes pitch-shift naturally",
                  "Phase-offset sync for precise loop placement",
                  "Multi-stem export (1–16 cycles, background render)",
                  "AU plugin and Standalone app",
                  "Full DAW integration — reads transport and BPM",
                ]}
              />
            </div>
          </div>

          {/* Comparison table */}
          <div className="border-t pt-10">
            <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground mb-6">
              How Cobar compares
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr>
                    <th className="text-left text-xs font-medium uppercase tracking-wider text-muted-foreground pb-4 pr-6 w-48">
                      Feature
                    </th>
                    {COMPETITORS.map((c, i) => (
                      <th
                        key={c}
                        className={`text-center text-xs font-medium uppercase tracking-wider pb-4 px-4 ${
                          i === 0
                            ? "text-foreground"
                            : "text-muted-foreground/60"
                        }`}
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, rowIdx) => (
                    <tr
                      key={row.label}
                      className={
                        rowIdx % 2 === 0 ? "bg-[#191919]/60" : "bg-transparent"
                      }
                    >
                      <td className="py-3 pr-6 text-xs text-muted-foreground font-medium uppercase tracking-wider">
                        {row.label}
                      </td>
                      {row.values.map((val, colIdx) => (
                        <td
                          key={colIdx}
                          className={`py-3 px-4 text-center ${
                            colIdx === 0 ? "font-medium" : ""
                          }`}
                        >
                          <Cell value={val} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground/40 mt-4">
              Comparison reflects publicly available information as of early
              2026. Some capabilities may vary by version or configuration.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
